// BookingDetails.tsx
import React from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { parseISO, format } from "date-fns";
import { useGuestyReservation } from "hooks/useGuestyReservation";
import PaymentHandling from "./PaymentHandling";

interface BookingDetailsProps {
  Quote: any;
  providerId: string;
  listingID: string;
}

const BookingDetails: React.FC<BookingDetailsProps> = ({
  Quote,
  providerId,
  listingID,
}) => {
  // Get the reservation from your custom hook
  const { reservation } = useGuestyReservation();

  /**
   * Safely parse the reservation’s confirmedAt date (if it exists).
   * Otherwise, fallback to "N/A".
   */
  const getConfirmationDateFormatted = () => {
    if (!reservation?.confirmedAt) {
      return "N/A";
    }
    try {
      const dateObj = parseISO(reservation.confirmedAt);
      return format(dateObj, "yyyy-MM-dd");
    } catch (error) {
      console.error("Invalid date string for confirmedAt:", error);
      return "N/A";
    }
  };

  const renderReservationDetails = () => (
    <div className="space-y-6">
      <h3 className="text-2xl font-semibold">Booking details</h3>
      <div className="border-b border-neutral-200 dark:border-neutral-700"></div>

      <div className="flex flex-col space-y-4">
        {/* Booking confirmation number */}
        <div className="flex text-neutral-600 dark:text-neutral-300">
          <span className="flex-1">Booking confirmation number</span>
          <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
            {reservation?.confirmationCode || "N/A"}
          </span>
        </div>

        {/* Number of guests */}
        <div className="flex text-neutral-600 dark:text-neutral-300">
          <span className="flex-1">Number of guests</span>
          <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
            {reservation?.guestsCount || "N/A"}
          </span>
        </div>

        {/* Check-in Date */}
        <div className="flex text-neutral-600 dark:text-neutral-300">
          <span className="flex-1">Check-in Date</span>
          <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
            {reservation?.checkInDateLocalized || "N/A"}
          </span>
        </div>

        {/* Check-out Date */}
        <div className="flex text-neutral-600 dark:text-neutral-300">
          <span className="flex-1">Check-out Date</span>
          <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
            {reservation?.checkOutDateLocalized || "N/A"}
          </span>
        </div>

        {/* Payment method */}
        <div className="flex text-neutral-600 dark:text-neutral-300">
          <span className="flex-1">Payment method</span>
          <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
            Credit card
          </span>
        </div>

        {/* Payment date */}
        <div className="flex text-neutral-600 dark:text-neutral-300">
          <span className="flex-1">Payment date</span>
          <span className="flex-1 font-medium text-neutral-900 dark:text-neutral-100">
            {getConfirmationDateFormatted()}
          </span>
        </div>
      </div>
    </div>
  );

  const renderPaymentSection = () => (
    <div>
      <h2 className="text-3xl lg:text-4xl font-semibold">Confirmation And Payment</h2>
      <div>
        <PaymentHandling
          Quote={Quote}
          providerId={providerId}
          listingID={listingID || ""}
        />
      </div>
    </div>
  );

  const renderConfirmationSection = () => (
    <div>
      <h2 className="text-3xl lg:text-4xl font-semibold">Congratulations 🎉</h2>
      {renderReservationDetails()}
      <div>
        <ButtonPrimary href="/listings">Explore more stays</ButtonPrimary>
      </div>
    </div>
  );

  // If not confirmed, show the payment step. Otherwise, show the confirmation.
  return (
    <div className="w-full lg:w-1/2 lg:mr-6">
      <div className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-8 px-0 sm:p-6 xl:p-8">
        {reservation?.status !== "confirmed"
          ? renderPaymentSection()
          : renderConfirmationSection()}
      </div>
    </div>
  );
};

export default BookingDetails;